import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import type { IncidentType } from '@/types';

import IncidentSummary from '@/components/Incidents/incident-summary';
import CustomAccordion from '@/components/ui/custom-accordion';
import { cn } from '@/lib/utils';
import { LABELS, OTHER_RELATED_INCIDENTS } from '@/utils/constants';
import { SEARCH_PARAM_INCIDENT_ID } from '@/utils/constants/queries';

interface OtherRelatedIncidentsProps {
  incidents: IncidentType[];
}

const OtherRelatedIncidents: FC<OtherRelatedIncidentsProps> = ({ incidents }) => {
  const [, setSearchParams] = useSearchParams();
  const [expandedValue, setExpandedValue] = useState<string[]>([OTHER_RELATED_INCIDENTS]);
  const { t } = useTranslation();

  if (!incidents?.length) {
    return null;
  }

  const handleIncidentClick = (incidentId: string) => {
    setSearchParams((prev) => {
      prev.set(SEARCH_PARAM_INCIDENT_ID, incidentId);

      return prev;
    });
  };

  const handleAccordionChange = (value: string[]) => {
    setExpandedValue(value);
  };

  const isExpanded = expandedValue.includes(OTHER_RELATED_INCIDENTS);

  const accordionItems = [
    {
      value: OTHER_RELATED_INCIDENTS,
      title: t(LABELS.otherRelatedIncidents),
      content: (
        <div className="[&>*:not(:last-child)]:border-b">
          {incidents.map((incident) => (
            <div
              key={incident.gtfsAlertId}
              onClick={() => handleIncidentClick(incident.gtfsAlertId)}
              className="cursor-pointer hover:bg-accent/50 transition-colors"
            >
              <IncidentSummary incident={incident} />
            </div>
          ))}
        </div>
      ),
    },
  ];

  return (
    <div
      className={cn({
        'border-b border-surface-border-default pb-1': !isExpanded,
      })}
    >
      <CustomAccordion
        type="multiple"
        items={accordionItems}
        value={expandedValue}
        onValueChange={handleAccordionChange}
      />
    </div>
  );
};

export default OtherRelatedIncidents;
